import { useFlag } from '@/components/posthog-provider';
import { apiClient } from '@borradh-workspace/api-client';
import { STOCK_FOOTAGE_MATCHING_NEW_FLAG } from '@borradh-workspace/labels';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import { useState } from 'react';

import { VideoPlayer } from '@/components/kibo-ui/video-player/video-player';
import { Button } from '@/components/ui/button';
import { describeGraphicFailure } from '@/features/graphics/graphic-failure';
import { queryKeys } from '@/lib/query-keys';
import { AdArtifactPanel } from './ad-artifact-panel';
import type { OpenArtifact, OpenMediaArtifact } from './artifact-panel-context';
import { useArtifactPanel } from './artifact-panel-context';
import { ContentPanelActions } from './content-panel-actions';
import { GraphicCarousel } from './graphic-carousel';
import { ImageReveal } from './image-reveal';
import {
  MediaVersionSelector,
  useIsWaitingForMediaFork,
  useSelectMediaVersion,
  useSelectedMediaVersion,
} from './media-version-selection';
import {
  VideoDraftPreviewCard,
  VideoEditorButton,
} from './rich/video-draft-preview';
import { creativeSlides, useCreativeAsset } from './use-creative-asset';

/**
 * The finished render, beside the conversation rather than inside it.
 *
 * A render takes anywhere from seconds to a couple of minutes, and a chat is
 * the wrong shape to wait in: the card that started it scrolls away as soon as
 * anything else is said, so the owner either stops talking to keep it in view
 * or loses track of it. A panel holds still.
 *
 * It POLLS RATHER THAN BEING TOLD. The render outlives the turn that started
 * it — usually the card too — so the only durable handle is the identity, and
 * everything else is read back from the server. That also means a reload or a
 * second edit lands somewhere sensible: the panel follows whatever it was last
 * given, including the FORK an edit produces.
 *
 * Videos and graphics share it because they are the same thing to the owner —
 * a finished piece of content to look at. Only the endpoint and the player
 * differ, and both of those are one branch. An AD is that plus the copy
 * around it, read from a different endpoint, so it gets its own panel behind
 * the same `kind` switch — the row that opens it does not care which.
 */
export function ArtifactPanel({
  artifact,
  onDecided,
  onSendMessage,
  closable = true,
}: {
  artifact: OpenArtifact;
  /** Called after Save / Schedule / Reject, so a queue can advance. */
  onDecided?: () => void;
  /**
   * How an ad's edit pencils send their request. Only the ad panel uses it;
   * without it the pencils are simply absent (the review page, for one, has
   * no composer to send through).
   */
  onSendMessage?: (text: string) => void;
  /**
   * Whether the panel can be dismissed.
   *
   * False on the review page, where the panel is a permanent column — closing
   * it there would leave a review screen with nothing to review and no way
   * back except picking another post.
   */
  closable?: boolean;
}) {
  if (artifact.kind === 'ad') {
    return (
      <AdArtifactPanel
        adId={artifact.id}
        onSendMessage={onSendMessage}
        closable={closable}
      />
    );
  }
  return (
    <MediaArtifactPanel
      artifact={artifact}
      onDecided={onDecided}
      closable={closable}
    />
  );
}

function MediaArtifactPanel({
  artifact,
  onDecided,
  closable,
}: {
  artifact: OpenMediaArtifact;
  onDecided?: () => void;
  closable: boolean;
}) {
  const { close, openArtifact } = useArtifactPanel();
  const selectVersion = useSelectMediaVersion();
  // Rejected: nothing left to look at, so a closable panel closes — and
  // `close` marks the artifact dismissed, so a render that was still in
  // progress does not reopen it when it lands. The review page's permanent
  // column instead advances to the next post through `onDecided`.
  const onRejected = closable ? close : undefined;
  const [slideByItem, setSlideByItem] = useState<Record<string, number>>({});
  const slideKey = artifact.itemId ?? artifact.id;
  const activeSlide = slideByItem[slideKey] ?? 0;
  const selectSlide = (index: number) =>
    setSlideByItem((current) => ({ ...current, [slideKey]: index }));
  const v2Enabled = useFlag(STOCK_FOOTAGE_MATCHING_NEW_FLAG);
  const isVideo = artifact.kind === 'video';
  const selected = useSelectedMediaVersion(artifact.itemId);
  const waitingForFork = useIsWaitingForMediaFork(artifact.itemId);
  const selectedId =
    selected?.kind === artifact.kind ? selected.assetId : artifact.id;

  // Same key the cards poll on, so one request feeds both and they can never
  // disagree about whether the render has finished.
  const {
    asset: data,
    pending: rendering,
    failed,
    isError,
    refetch,
  } = useCreativeAsset({ kind: artifact.kind, id: selectedId });
  const { data: itemState } = useQuery({
    queryKey: queryKeys.contentBatches.itemState(
      artifact.itemId ?? '',
      selectedId
    ),
    queryFn: () =>
      apiClient.get<{ assetId: string | null; reviewStatus: string }>(
        `content-batches/items/${artifact.itemId}/state`
      ),
    enabled: Boolean(artifact.itemId),
    staleTime: 0,
  });

  const status = data?.status ?? 'queued';
  const images = creativeSlides(data);
  // Graphics carry a stable failure code; a safety block (ENG-1155) gets its
  // own heading here, matching the chat card and the content library.
  const graphicFailure =
    failed && !isVideo
      ? describeGraphicFailure(data ?? {}, 'Ask Claire to try it again.')
      : null;

  return (
    <div className="flex h-full min-h-0 flex-col bg-muted/30">
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2">
        <p className="min-w-0 flex-1 truncate text-sm font-medium">
          {data?.title ?? 'Media preview'}
        </p>
        {artifact.itemId &&
        !waitingForFork &&
        itemState?.reviewStatus === 'pending' &&
        itemState.assetId === selectedId ? (
          <div className="order-last w-full">
            <ContentPanelActions
              itemId={artifact.itemId}
              assetId={selectedId}
              mediaReady={status === 'ready'}
              onRejected={onRejected}
              onDecided={onDecided}
            />
          </div>
        ) : null}
        {isVideo &&
        v2Enabled &&
        data?.schemaVersion === 2 &&
        !isError &&
        !rendering ? (
          <VideoEditorButton
            videoId={selectedId}
            onSaved={(result) => {
              selectVersion?.({
                itemId: result.itemId,
                kind: 'video',
                assetId: result.video.id,
              });
              openArtifact({
                kind: 'video',
                id: result.video.id,
                itemId: result.itemId,
              });
            }}
          />
        ) : null}
        {artifact.itemId ? (
          <MediaVersionSelector
            itemId={artifact.itemId}
            kind={artifact.kind}
            assetId={artifact.id}
          />
        ) : null}
        {closable ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            onClick={close}
            aria-label="Close panel"
          >
            <X className="size-4" />
          </Button>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto p-4">
        {isError ? (
          <div
            className="flex flex-col items-center gap-2 text-center"
            role="alert"
          >
            <p className="text-sm">The preview could not be loaded.</p>
            <Button
              type="button"
              variant="outline"
              onClick={() => void refetch()}
            >
              Try again
            </Button>
          </div>
        ) : isVideo &&
          v2Enabled &&
          data?.schemaVersion === 2 &&
          status === 'draft' ? (
          <VideoDraftPreviewCard
            key={selectedId}
            id={`panel-draft-${selectedId}`}
            videoId={selectedId}
            title={data?.title ?? 'Video draft'}
            fields={[]}
            initialClipAssetIds={[]}
          />
        ) : failed ? (
          <div className="flex max-w-xs flex-col items-center gap-2 text-center">
            <AlertTriangle className="size-6 text-destructive" />
            <p className="text-sm font-medium">
              {graphicFailure?.isSafetyBlock
                ? graphicFailure.title
                : 'That render failed.'}
            </p>
            <p className="text-xs text-muted-foreground">
              {graphicFailure?.message ??
                data?.errorMessage ??
                'Ask Claire to try it again.'}
            </p>
            {graphicFailure?.code ? (
              <p className="text-xs font-medium">
                Error code:{' '}
                <span className="select-all font-mono">
                  {graphicFailure.code}
                </span>
              </p>
            ) : null}
          </div>
        ) : isVideo && rendering ? (
          /* The pulsing wait, in the shape of the render: portrait unless
             the video says otherwise. */
          <ImageReveal
            aspectRatio={data?.aspectRatio ?? '9:16'}
            caption={
              status === 'queued'
                ? 'Queued for rendering. Waiting for a render slot.'
                : data?.processingStage
                  ? `${data.processingStage}…`
                  : 'Rendering — usually a minute or two.'
            }
            progress={data?.progress}
          />
        ) : isVideo && data?.blobUrl ? (
          /* `max-h-full` and not just `max-w-sm`: a portrait video sized only
             by width runs off the bottom of the panel. Bounding both and
             letting the player take its shape from the media is what keeps a
             9:16 render whole without letterboxing it into a 16:9 box. */
          <div className="flex h-full min-h-0 w-full max-w-sm animate-in items-center justify-center fade-in-0 duration-500 motion-reduce:animate-none">
            <VideoPlayer
              key={selectedId}
              fitToMedia
              src={data.blobUrl}
              poster={data.thumbnailUrl ?? undefined}
              className="max-h-full w-auto"
              onRetry={() => refetch()}
            />
          </div>
        ) : !isVideo && (rendering || images.length === 1) ? (
          /* One frame from the wait to the finished image: the placeholder
             is not swapped for an `<img>`, the image fills it, blurred and
             then sharp. Keyed on the version, so picking another version
             reveals that one afresh. */
          <ImageReveal
            key={selectedId}
            src={rendering ? undefined : images[0]?.url}
            previewSrc={rendering ? undefined : images[0]?.thumbnailUrl}
            aspectRatio={data?.aspectRatio ?? '1:1'}
            caption={
              data?.processingStage
                ? `${data.processingStage}…`
                : 'Rendering — images can take a few minutes.'
            }
            progress={data?.progress}
          />
        ) : !isVideo && images.length > 1 ? (
          /* A deck is read in order, so it keeps its prev/next, dots and
             counter rather than becoming a vertical stack. */
          <div className="w-full max-w-sm animate-in fade-in-0 duration-500 motion-reduce:animate-none">
            <GraphicCarousel
              slides={images}
              index={activeSlide}
              onSelect={selectSlide}
            />
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Nothing to show for this one yet.
          </p>
        )}
      </div>
    </div>
  );
}
